import React, { useEffect, useState } from 'react';
import { Input, Table, Button, Stack, IconButton, Grid, Col, Row, Checkbox, Divider, SortType } from 'rsuite';
import { BaseErrorModel, LatLng, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import { CityDistrict, CityDistrictPaginationFilterRequest } from '@/models/city-district.model';
import { getPagedCityDistricts } from '@/services/city-district.service';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { hk_lat_lng } from '@/constants/map';
import { getBoundingBoxCenter, getPolygonPathsFromGeoJson } from '@/utils/map';
import { getHeight } from 'rsuite/esm/DOMHelper';

const { Column, HeaderCell, Cell } = Table;

interface CityDistrictPolygonPath extends CityDistrict{
  paths: LatLng[][];
  color: string;
}
interface CityDistrictListProps {
  cityId: number;
  mapCenter?: LatLng;
}
const CityDistrictList : React.FC<CityDistrictListProps> = ({cityId, mapCenter : mapCenterProp}) => {
  const [filterReq, setFilterReq] = useState<CityDistrictPaginationFilterRequest>(new CityDistrictPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<CityDistrict[]>([]);
  const [selectedItems, setSelectedItems] = useState<CityDistrictPolygonPath[]>([]);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState<LatLng|undefined>();
  const [checkAll, setCheckAll] = useState<boolean>(false);

  const nav = useNavigate();
  const { isLoaded } = useJsApiLoader({
      id: 'google-map-script',
      googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
    });
    
  useEffect(() => {
    if(mapCenterProp){
      setMapCenter(mapCenterProp);
    }
  }, []);
    
  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: CityDistrictPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        cityId: cityId,
      };   
      const result: PagedResponseModel<CityDistrict> = await getPagedCityDistricts(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
        setSelectedItems([]); // Reset selected items on new fetch
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Discovery Points:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };
  const handleCheckAll = (value, checked) => {
    setSelectedItems(checked ? items.map(item => ({
      ...item, 
      paths: getPolygonPathsFromGeoJson(item.jsonGeometry),
      color: getRandomColor()
    })) : []);
    const pathsFlat = checked ? items.map(itm => getPolygonPathsFromGeoJson(itm.jsonGeometry)).flat(2) : [];
    setMapCenter(getBoundingBoxCenter(pathsFlat));
    setCheckAll(checked);
  };

  const onItemCheckChange = (rowData: CityDistrict, chk: boolean) => {
    let newSelectedItems : CityDistrictPolygonPath[] = [];
    if(chk){
      const newItem: CityDistrictPolygonPath = {
        ...rowData, paths: 
        getPolygonPathsFromGeoJson(rowData.jsonGeometry),
        color: getRandomColor()
      };
      newSelectedItems = [...selectedItems, newItem];
    } else {
      newSelectedItems = selectedItems.filter(i => i.id !== rowData.id);
    }

    const pathsFlat = newSelectedItems.map(itm => itm.paths).flat(2);

    setSelectedItems(newSelectedItems);
    setMapCenter(getBoundingBoxCenter(pathsFlat));
  };
  // Utility to generate a random hex color
  const getRandomColor = () => '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>   
          <Button appearance="primary" onClick={() => nav(`/cities/districts/new`, {state: {cityId}})}>
            Add Single District
          </Button>
          <Button appearance="primary" onClick={() => nav(`/cities/districts/multiple/new`, {state: {cityId}})}>
            Add Multiple District
          </Button>
        </Stack>

        <Stack spacing={6}>   
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />    
        </Stack>
      </Stack>      
      <Grid w='100%' fluid>
        <Row>
          <Col span={{xs:24, xl:8}}>
            <Table
              height={Math.max(getHeight(window) - 350, 400)}
              data={items}
              sortColumn={filterReq.orderBy}
              sortType={filterReq.isAscending ? 'asc' : 'desc'}
              onSortColumn={handleSortColumn}
              loading={loading}
            >
              <Column flexGrow={0.5} align="center" >
                <HeaderCell style={{ padding: 0 }}>
                  <div style={{ lineHeight: '40px' }}>
                    <Checkbox
                      inline
                      checked={checkAll}
                      onChange={handleCheckAll}
                    />
                  </div>                  
                </HeaderCell>
                <Cell style={{ padding: 0 }}>
                  {(rowData: CityDistrict) =>
                    <div style={{ lineHeight: '46px' }}>
                      <Checkbox
                        inline
                        onChange={(v, chk) => onItemCheckChange(rowData, chk)}
                        checked={selectedItems.some(i => i.id == rowData.id)}
                      /> 
                    </div>
                  }
                </Cell>
              </Column>              
              <Column flexGrow={2} align="left" fullText sortable>
                <HeaderCell>Name</HeaderCell>
                <Cell dataKey="name" />
              </Column>
              <Column flexGrow={0.5} fixed="right">
                <HeaderCell>...</HeaderCell>

                <Cell style={{ padding: '6px' }}>
                  {(rowData : CityDistrict) => (              
                    <IconButton onClick={() => nav(`/cities/districts/${rowData.id}`, {state: {cityId}})} icon={<EditIcon />} circle size="sm" />
                  )}
                </Cell>
              </Column>

            </Table>
            <Divider />
            <TablePagination 
              totalRecords={pageRes.totalRecords}
              pageSize={pageRes.pageSize}
              activePage={pageRes.pageNumber}
              onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
              onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
            />          
          </Col>
          <Col span={{xs:24, xl:16}}>
            {isLoaded ? <GoogleMap
              mapContainerStyle={{ width: '100%', height: `${Math.max(getHeight(window) - 270, 700)}px`, borderRadius: 10 }}
              center={mapCenter || hk_lat_lng}
              zoom={12}
            >
              {selectedItems.map(item => 
                <React.Fragment key={item.id}>
                  <Polygon 
                    paths={item.paths} 
                    options={{
                      fillColor: item.color,
                      strokeColor: item.color,
                      strokeOpacity: 1.0,
                      strokeWeight: 4,
                    }}
                  />
                </React.Fragment>)}  
            </GoogleMap> : '' } 
          </Col>
        </Row>
      </Grid>
    </PageContent>

  );
};

export default CityDistrictList;
